import React, { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { InstrumentHoverCard } from "./InstrumentHoverCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { 
  Check, Plus, X, Music, Sparkles, ChevronDown,
  Guitar, Piano, Mic2, Drum, Music2, Music4, AlertCircle,
  type LucideIcon
} from "lucide-react";
import { musicianServices } from "@/lib/pricingData";
import { cn } from "@/lib/utils";

// Instrument to icon mapping
const INSTRUMENT_ICONS: Record<string, LucideIcon> = {
  // Strings
  "Guitar": Guitar,
  "Acoustic Guitarists": Guitar,
  "Electric Guitarists": Guitar,
  "Guitarists": Guitar,
  "Violin": Music2,
  "Violinists": Music2,
  "Harp": Music4,
  "Harpists": Music4,
  "Cellist": Music2,
  "Cello": Music2,
  "Bass": Guitar,
  "Bassist": Guitar,
  "Sitar Players": Music4,
  "Dilruba Players": Music2,
  // Keys
  "Piano": Piano,
  "Pianists": Piano,
  "Keyboard Players": Piano,
  "Keys": Piano,
  // Drums & Percussion
  "Drums": Drum,
  "Drummers": Drum,
  "Dhol Players": Drum,
  "Tabla Players": Drum,
  "Percussionists": Drum,
  "Handpan Players": Drum,
  // Wind
  "Saxophone": Music2,
  "Saxophonists": Music2,
  "Trumpet": Music2,
  "Trumpeters": Music2,
  "Flautists": Music2,
  "Flute": Music2,
  "Bansuri Players": Music2,
  "Shehnai Players": Music2,
  "Tumbi Players": Music4,
  // Vocals
  "Vocalist": Mic2,
  "Vocalists": Mic2,
  "Singer": Mic2,
  // Other
  "DJ": Music,
};

const getInstrumentIcon = (instrument: string): LucideIcon => {
  // Try exact match first
  if (INSTRUMENT_ICONS[instrument]) {
    return INSTRUMENT_ICONS[instrument];
  }
  // Try partial match
  const lowerInst = instrument.toLowerCase();
  for (const [key, icon] of Object.entries(INSTRUMENT_ICONS)) {
    if (lowerInst.includes(key.toLowerCase()) || key.toLowerCase().includes(lowerInst)) {
      return icon;
    }
  }
  return Music; // Default icon
};

const normalizeInstrumentList = (arr: string[] | null | undefined): string[] => {
  if (!arr) return [];
  const flattened = arr
    .flatMap((item) => {
      if (typeof item === "string" && item.includes(",")) {
        return item
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return item;
    })
    .filter(Boolean);
  return [...new Set(flattened)];
};

interface FinalInstrumentEditorProps {
  eventIndex: number;
  recommendedInstruments: string[];
  packageInstruments: string[];
  finalInstruments: string[];
  requiredEnsembleSize?: number;
  onSave: (instruments: string[]) => void;
  onPushToContract?: () => void;
  isLoading?: boolean;
  eventName?: string;
  eventDate?: string;
  venue?: string;
  inquiryId?: string;
  inquiryRegion?: string;
  clientName?: string;
  clientEmail?: string;
  onDocumentSaved?: () => void;
  onMusicianAssigned?: () => void;
  musicianAssignments?: any[];
}

// All available instruments from pricing data plus additional common ones
const ALL_INSTRUMENTS = [
  ...musicianServices.map(m => m.name),
  "Bansuri Players",
  "Dilruba Players",
  "Handpan Players",
  "Tumbi Players",
  "Sitar Players",
  "Flautists",
  "Percussionists",
  "DJ",
  "Vocalist",
  "Acoustic Guitarists",
  "Electric Guitarists",
  "Keyboard Players",
];

const INSTRUMENT_OPTIONS = [...new Set(ALL_INSTRUMENTS)].sort();

export function FinalInstrumentEditor({
  eventIndex,
  recommendedInstruments = [],
  packageInstruments = [],
  finalInstruments = [],
  requiredEnsembleSize,
  onSave,
  isLoading = false,
  inquiryId,
  inquiryRegion,
  eventDate,
  onMusicianAssigned,
  musicianAssignments = [],
}: FinalInstrumentEditorProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  
  const maxInstruments = requiredEnsembleSize || 6;

  const normalizedFinal = useMemo(
    () => normalizeInstrumentList(finalInstruments),
    [finalInstruments]
  );

  const normalizedRecommended = useMemo(
    () => normalizeInstrumentList(recommendedInstruments),
    [recommendedInstruments]
  );
  
  const normalizedPackage = useMemo(
    () => normalizeInstrumentList(packageInstruments),
    [packageInstruments]
  );

  const suggestedInstruments = useMemo(() => {
    return [...new Set([...normalizedRecommended, ...normalizedPackage])].filter(Boolean);
  }, [normalizedRecommended, normalizedPackage]);

  // Use final instruments if set, otherwise use suggestions
  const displayInstruments = normalizedFinal.length > 0 ? normalizedFinal : suggestedInstruments;
  const hasCustomized = normalizedFinal.length > 0;
  const isComplete = displayInstruments.length >= maxInstruments;
  const spotsRemaining = maxInstruments - displayInstruments.length;

  const isAIRecommended = (inst: string): boolean => normalizedRecommended.includes(inst);

  // Filter instruments for dropdown
  const filteredInstruments = useMemo(() => {
    let filtered = INSTRUMENT_OPTIONS.filter(inst => !displayInstruments.includes(inst));
    if (searchQuery) {
      filtered = filtered.filter(inst => 
        inst.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return filtered;
  }, [searchQuery, displayInstruments]);

  // Quick suggestions (not yet selected)
  const quickSuggestions = useMemo(() => {
    return suggestedInstruments.filter(inst => !displayInstruments.includes(inst));
  }, [suggestedInstruments, displayInstruments]);

  const handleAddInstrument = (instrument: string) => {
    if (displayInstruments.length >= maxInstruments) return;
    const updated = [...displayInstruments, instrument];
    onSave(updated);
  };

  const handleRemoveInstrument = (instrument: string) => {
    const updated = displayInstruments.filter(i => i !== instrument);
    onSave(updated);
  };

  const handleAddCustom = () => {
    if (searchQuery.trim() && !displayInstruments.includes(searchQuery.trim())) {
      handleAddInstrument(searchQuery.trim());
      setSearchQuery("");
      setIsPopoverOpen(false);
    }
  };

  // Apply all suggestions at once
  const handleApplySuggestions = () => {
    const toAdd = suggestedInstruments.slice(0, maxInstruments);
    onSave(toAdd);
  };

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Music className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium">
            {hasCustomized ? 'Final Ensemble' : 'Suggested Ensemble'}
          </span>
          <Badge 
            variant={isComplete ? "default" : "secondary"} 
            className={cn(
              "text-[10px] h-5 font-mono",
              isComplete && "bg-green-600 hover:bg-green-600"
            )}
          >
            {displayInstruments.length}/{maxInstruments}
          </Badge>
        </div>

        {/* Quick actions */}
        <div className="flex items-center gap-1">
          {!hasCustomized && suggestedInstruments.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="h-6 px-2 text-[10px]"
              onClick={handleApplySuggestions}
            >
              <Check className="h-3 w-3 mr-1" />
              Apply Suggestions
            </Button>
          )}
          
          {/* Add instrument popover */}
          {!isComplete && (
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 px-2 text-[10px] gap-1"
                >
                  <Plus className="h-3 w-3" />
                  Add
                  <ChevronDown className="h-2.5 w-2.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0 bg-popover border shadow-lg z-50" align="end">
                {/* Search */}
                <div className="p-2 border-b">
                  <Input
                    placeholder="Search or type custom..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAddCustom();
                      }
                    }}
                    className="h-8 text-xs"
                    autoFocus
                  />
                </div>

                {/* Quick suggestions */}
                {quickSuggestions.length > 0 && !searchQuery && (
                  <div className="p-2 border-b bg-muted/30">
                    <p className="text-[10px] text-muted-foreground mb-1.5 flex items-center gap-1">
                      <Sparkles className="h-3 w-3 text-amber-500" />
                      AI Recommended
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {quickSuggestions.map((inst) => {
                        const InstIcon = getInstrumentIcon(inst);
                        return (
                          <Badge
                            key={inst}
                            variant="outline"
                            className="text-[10px] cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors gap-1"
                            onClick={() => {
                              handleAddInstrument(inst);
                              if (displayInstruments.length + 1 >= maxInstruments) {
                                setIsPopoverOpen(false);
                              }
                            }}
                          >
                            <InstIcon className="h-2.5 w-2.5 opacity-60" />
                            <Plus className="h-2.5 w-2.5" />
                            {inst}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* All instruments list */}
                <ScrollArea className="h-48">
                  <div className="p-1">
                    {filteredInstruments.length > 0 ? (
                      filteredInstruments.map((inst) => {
                        const InstIcon = getInstrumentIcon(inst);
                        return (
                          <button
                            key={inst}
                            type="button"
                            className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent transition-colors flex items-center gap-2"
                            onClick={() => {
                              handleAddInstrument(inst);
                              setSearchQuery("");
                              if (displayInstruments.length + 1 >= maxInstruments) {
                                setIsPopoverOpen(false);
                              }
                            }}
                          >
                            <InstIcon className="h-3.5 w-3.5 text-muted-foreground" />
                            {inst}
                            <Plus className="h-3 w-3 text-muted-foreground ml-auto" />
                          </button>
                        );
                      })
                    ) : searchQuery ? (
                      <button
                        type="button"
                        className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent transition-colors flex items-center gap-2 text-primary"
                        onClick={handleAddCustom}
                      >
                        <Plus className="h-3 w-3" />
                        Add "{searchQuery}" as custom
                      </button>
                    ) : (
                      <p className="text-xs text-muted-foreground p-2 text-center">No more instruments available</p>
                    )}
                  </div>
                </ScrollArea>

                {/* Footer with count */}
                <div className="p-2 border-t bg-muted/20 text-center">
                  <span className="text-[10px] text-muted-foreground">
                    {spotsRemaining > 0 ? `${spotsRemaining} spot${spotsRemaining > 1 ? 's' : ''} remaining` : 'Ensemble complete'}
                  </span>
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {/* Instrument badges */}
      {displayInstruments.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {displayInstruments.map((inst, i) => {
            const isFromAI = isAIRecommended(inst);
            const InstrumentIcon = getInstrumentIcon(inst);
            // Check if this instrument has a musician assigned
            const isAssigned = inquiryId ? musicianAssignments.some(
              (a: any) =>
                a.event_index === eventIndex &&
                a.instrument?.toLowerCase() === inst.toLowerCase() &&
                a.status !== "cancelled"
            ) : true; // If no inquiryId, don't show unassigned indicator

            const badgeEl = (
                <Badge 
                  key={i} 
                  variant={hasCustomized ? "default" : "secondary"} 
                  className={cn(
                    "text-xs gap-1 pr-0.5 group cursor-pointer",
                    hasCustomized && isAssigned && "bg-primary",
                    hasCustomized && !isAssigned && "bg-amber-500/15 border-amber-400 text-amber-700 dark:text-amber-400 border",
                    !hasCustomized && !isAssigned && "bg-amber-500/10 border-amber-400 text-amber-700 dark:text-amber-400 border"
                  )}
                >
                  {!isAssigned && <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />}
                  <InstrumentIcon className="h-3 w-3 opacity-70" />
                  {isFromAI && <Sparkles className="h-2.5 w-2.5 text-amber-300" />}
                  {inst}
                  <button
                    type="button"
                    className="ml-0.5 p-0.5 rounded-full opacity-60 hover:opacity-100 hover:bg-background/20 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveInstrument(inst);
                    }}
                    disabled={isLoading}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );

              if (inquiryId) {
                // Find existing assignment for this instrument + event
                const assignment = musicianAssignments.find(
                  (a: any) =>
                    a.event_index === eventIndex &&
                    a.instrument?.toLowerCase() === inst.toLowerCase() &&
                    a.status !== "cancelled"
                );
                return (
                  <InstrumentHoverCard
                    key={i}
                    instrument={inst}
                    inquiryId={inquiryId}
                    eventIndex={eventIndex}
                    inquiryRegion={inquiryRegion}
                    eventDate={eventDate}
                    existingAssignment={assignment || null}
                    onMusicianAssigned={onMusicianAssigned}
                  >
                    {badgeEl}
                  </InstrumentHoverCard>
                );
              }
              return badgeEl;
          })}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic">Click "Add" to select instruments</p>
      )}

      {/* Helpful hint when not complete */}
      {!isComplete && displayInstruments.length > 0 && (
        <p className="text-[10px] text-destructive font-medium flex items-center gap-1 mt-1 bg-destructive/10 p-1 rounded">
          <AlertCircle className="h-3 w-3" />
          Ensemble incomplete: Add {spotsRemaining} more instrument{spotsRemaining !== 1 ? 's' : ''} to match package
        </p>
      )}
    </div>
  );
}
